import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { X, Download } from 'lucide-react';

/**
 * PWA "Add to Home Screen" prompt.
 *
 * Appears 5 seconds after the browser fires `beforeinstallprompt` (Chrome
 * Android + desktop Chromium). Users who've dismissed it once won't see
 * it again this session — we honor localStorage['pwa-prompt-dismissed'].
 *
 * Palette matches the site's peach/chocolate theme. The decorative
 * mini-grid on the left shows a few "clue" cells to reinforce the
 * Sudoku brand at a glance.
 */
export default function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 5000);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShowPrompt(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-[var(--background-surface)] border-2 border-[#8B5A3C]/40 rounded-xl p-4 shadow-2xl z-50 animate-slide-up"
      data-testid="install-prompt"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-[var(--text-disabled)] hover:text-[var(--text-primary)] transition-colors"
        data-testid="dismiss-install"
        aria-label="Dismiss install prompt"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        {/* 9-cell mini-grid decoration — a few cells filled to hint at
            a Sudoku puzzle without being noisy at this size. */}
        <MiniSudokuGrid />
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-semibold text-[var(--text-primary)] mb-1">
            Install Sudoku Academy
          </h3>
          <p className="text-sm text-[var(--text-secondary)] mb-3">
            Add to your home screen for the best experience.
          </p>
          <Button
            onClick={handleInstall}
            className="bg-[#8B5A3C] hover:bg-[#A46944] text-white w-full"
            data-testid="install-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Install App
          </Button>
        </div>
      </div>
    </div>
  );
}

// Compact 3x3 (visual 9-cell) grid rendered in SVG for crisp scaling.
// A few cells contain "clue" numbers in dark cocoa to signal Sudoku.
function MiniSudokuGrid() {
  const cells = [
    { r: 0, c: 0, n: '5' },
    { r: 0, c: 2, n: '3' },
    { r: 1, c: 1, n: '7' },
    { r: 2, c: 0, n: '1' },
    { r: 2, c: 2, n: '9' },
  ];
  const SIZE = 48;
  const CELL = SIZE / 3;

  return (
    <svg
      width={SIZE}
      height={SIZE}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="flex-shrink-0 rounded"
      aria-hidden="true"
    >
      {/* Cream background matching landing card surface */}
      <rect width={SIZE} height={SIZE} fill="#FFF3E4" rx="4" />
      {/* Shade a couple of cells slightly so filled clues stand out */}
      <rect x={0} y={0} width={CELL} height={CELL} fill="#F5E2CB" />
      <rect x={CELL * 2} y={CELL * 2} width={CELL} height={CELL} fill="#F5E2CB" />
      {/* Grid lines — milk chocolate */}
      {[1, 2].map((i) => (
        <React.Fragment key={i}>
          <line
            x1={i * CELL} y1={0} x2={i * CELL} y2={SIZE}
            stroke="#8B5A3C" strokeWidth={i === 1 || i === 2 ? 1 : 0.5}
          />
          <line
            x1={0} y1={i * CELL} x2={SIZE} y2={i * CELL}
            stroke="#8B5A3C" strokeWidth={i === 1 || i === 2 ? 1 : 0.5}
          />
        </React.Fragment>
      ))}
      {/* Outer border thicker */}
      <rect
        x={0.5} y={0.5} width={SIZE - 1} height={SIZE - 1}
        fill="none" stroke="#3B2416" strokeWidth={1.5} rx={4}
      />
      {/* Clue numbers */}
      {cells.map(({ r, c, n }) => (
        <text
          key={`${r}-${c}`}
          x={c * CELL + CELL / 2}
          y={r * CELL + CELL / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#3B2416"
          fontSize={CELL * 0.6}
          fontWeight="700"
          fontFamily="ui-monospace, Menlo, monospace"
        >
          {n}
        </text>
      ))}
    </svg>
  );
}
